import { styled } from "@material-ui/core/styles";
import { brand } from "../theme";

export const PadTop = styled("div")(({ theme }) => ({
  padding: theme.spacing(2.25, 2, 0),
}));

export const Card = styled("div")({
  background: brand.white,
  borderRadius: 12,
  boxShadow: "0 1px 4px rgba(26,18,5,0.06)",
  overflow: "hidden",
});

// ── Group Accuracy summary card ─────────────────────────────
export const SummaryCard = styled(({ data, className }) => (
  <div className={className}>
    <div className="glow" aria-hidden />
    <div className="kicker">Group Accuracy</div>
    <div className="headline">
      <span className="pct">{data.accuracyPct}%</span>
      <span className="frac">
        {data.correctGuesses} of {data.totalGuesses} correct
      </span>
    </div>
    <div className="stats">
      {[
        { k: "Tasters", v: data.tastersCount },
        { k: "Rounds", v: data.roundsCount },
        { k: "Correct", v: data.correctGuesses },
      ].map((s) => (
        <div key={s.k} className="stat">
          <div className="stat-value">{s.v}</div>
          <div className="stat-key">{s.k}</div>
        </div>
      ))}
    </div>
  </div>
))(({ theme }) => ({
  margin: theme.spacing(1.75, 2, 0),
  borderRadius: 14,
  background: `linear-gradient(135deg, ${brand.amberDark} 0%, #2D1F0A 100%)`,
  padding: theme.spacing(2, 2.25),
  color: brand.white,
  boxShadow: "0 6px 24px rgba(26,18,5,0.18)",
  position: "relative",
  overflow: "hidden",

  "& .glow": {
    position: "absolute",
    right: -14,
    top: -14,
    width: 110,
    height: 110,
    borderRadius: "50%",
    background: `${brand.amber}18`,
  },
  "& .kicker": {
    fontSize: 10,
    fontWeight: 700,
    color: brand.amber,
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  "& .headline": {
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    marginTop: 4,
  },
  "& .pct": {
    fontSize: 38,
    fontWeight: 800,
    color: brand.white,
    letterSpacing: -1,
    lineHeight: 1,
  },
  "& .frac": {
    fontSize: 13,
    color: `${brand.amberFaint}AA`,
  },
  "& .stats": {
    display: "flex",
    gap: 8,
    marginTop: 14,
    position: "relative",
  },
  "& .stat": {
    flex: 1,
    padding: "8px 0 9px",
    borderRadius: 8,
    background: "rgba(255,255,255,0.07)",
    textAlign: "center",
  },
  "& .stat-value": {
    fontSize: 18,
    fontWeight: 800,
    color: brand.white,
    lineHeight: 1,
  },
  "& .stat-key": {
    fontSize: 10,
    color: `${brand.amberFaint}AA`,
    marginTop: 3,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    fontWeight: 600,
  },
}));

// ── Section header (reuses Ranking's style) ─────────────────
export const SectionHeader = styled(({ label, hint, className }) => (
  <div className={className}>
    <span className="label">{label}</span>
    {hint && <span className="hint">{hint}</span>}
  </div>
))(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1.25),
  padding: "0 2px",

  "& .label": {
    fontSize: 11,
    fontWeight: 800,
    color: brand.textSec,
    letterSpacing: 0.9,
    textTransform: "uppercase",
  },
  "& .hint": {
    fontSize: 10,
    color: brand.textDis,
    fontStyle: "italic",
  },
}));

// ── Round accuracy bar chart ─────────────────────────────────
export const AccuracyChart = styled(({ data, className }) => {
  const maxCorrect = Math.max(
    ...data.roundAccuracy.map((x: any) => x.correct),
    1,
  );
  return (
    <div className={className}>
      <div className="bars">
        {data.roundAccuracy.map((x: any) => {
          const h = data.tastersCount > 0 ? (x.correct / data.tastersCount) * 100 : 0;
          const isMax = x.correct === maxCorrect && x.correct > 0;
          const isZero = x.correct === 0;
          const bg = isZero
            ? `${brand.error}55`
            : isMax
            ? brand.amber
            : `${brand.amberDeep}66`;
          const labelColor = isZero
            ? brand.error
            : isMax
            ? brand.amberDeep
            : brand.textSec;
          return (
            <div key={x.round} className="col">
              <div className="col-fill">
                <div
                  className="bar"
                  style={{
                    height: isZero ? 3 : `${Math.max(h, 6)}%`,
                    background: bg,
                  }}
                >
                  <span className="count" style={{ color: labelColor }}>
                    {x.correct}
                  </span>
                </div>
              </div>
              <div className="tick">{x.round}</div>
            </div>
          );
        })}
      </div>
      <div className="legend">
        <span className="legend-item">
          <span className="dot" style={{ background: brand.amber }} /> Easiest
        </span>
        <span className="legend-item">
          <span
            className="dot"
            style={{ background: `${brand.amberDeep}66` }}
          />{" "}
          Some correct
        </span>
        <span className="legend-item">
          <span className="dot" style={{ background: `${brand.error}55` }} /> Nobody
        </span>
      </div>
    </div>
  );
})(({ theme }) => ({
  background: brand.white,
  borderRadius: 12,
  padding: theme.spacing(2, 1.75, 1.75),
  boxShadow: "0 1px 4px rgba(26,18,5,0.06)",

  "& .bars": {
    display: "flex",
    alignItems: "flex-end",
    gap: 6,
    height: 120,
  },
  "& .col": {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    height: "100%",
  },
  "& .col-fill": {
    flex: 1,
    display: "flex",
    alignItems: "flex-end",
    width: "100%",
  },
  "& .bar": {
    width: "100%",
    borderRadius: "4px 4px 2px 2px",
    position: "relative",
  },
  "& .count": {
    position: "absolute",
    top: -14,
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: 10,
    fontWeight: 700,
  },
  "& .tick": {
    fontSize: 9,
    color: brand.textDis,
    fontWeight: 600,
  },
  "& .legend": {
    display: "flex",
    gap: 12,
    marginTop: 12,
    paddingTop: 10,
    borderTop: `1px solid ${brand.sep}`,
    fontSize: 10,
    color: brand.textSec,
  },
  "& .legend-item": {
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  "& .dot": {
    width: 10,
    height: 10,
    borderRadius: 2,
    display: "inline-block",
  },
}));

// ── Toughest-round row ───────────────────────────────────────
export const ToughRow = styled(({ round, beer, isLast, of, className }) => (
  <div className={`${className}${isLast ? " is-last" : ""}`}>
    <div className="badge">R{round}</div>
    <div className="body">
      <div className="name">{beer}</div>
      <div className="sub">Nobody got it right</div>
    </div>
    <div className="score">0/{of}</div>
  </div>
))({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "11px 14px",
  borderBottom: `1px solid ${brand.sep}`,
  "&.is-last": { borderBottom: "none" },

  "& .badge": {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: brand.errorBg,
    color: brand.error,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 800,
    flexShrink: 0,
  },
  "& .body": { flex: 1, minWidth: 0 },
  "& .name": { fontSize: 14, fontWeight: 600, color: brand.text },
  "& .sub": { fontSize: 11, color: brand.textSec, marginTop: 1 },
  "& .score": { fontSize: 13, fontWeight: 700, color: brand.error, flexShrink: 0 },
});

// ── Easiest round highlight ──────────────────────────────────
export const EasiestCard = styled(({ round, beer, correct, of, className }) => (
  <div className={className}>
    <div className="badge" aria-hidden>
      🏆
    </div>
    <div className="body">
      <div className="title">
        Round {round} · {beer}
      </div>
      <div className="sub">
        {correct} of {of} got it right
      </div>
    </div>
  </div>
))({
  background: brand.amberLight,
  borderRadius: 12,
  padding: "14px 16px",
  border: `1px solid ${brand.amber}40`,
  display: "flex",
  alignItems: "center",
  gap: 14,

  "& .badge": {
    width: 46,
    height: 46,
    borderRadius: 13,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: `linear-gradient(135deg, ${brand.amber}, ${brand.amberDeep})`,
    boxShadow: `0 4px 14px ${brand.amber}55`,
    fontSize: 22,
    flexShrink: 0,
  },
  "& .body": { flex: 1, minWidth: 0 },
  "& .title": {
    fontSize: 15,
    fontWeight: 800,
    color: brand.amberDark,
  },
  "& .sub": {
    fontSize: 12,
    color: brand.amberDeep,
    marginTop: 2,
  },
});

// ── Halves comparison ───────────────────────────────────────
export const HalvesCard = styled(({ data, className }) => {
  const firstPct = data.halves.first.pct || 0;
  const secondPct = data.halves.second.pct || 0;
  const liftPct =
    firstPct > 0 ? Math.round((secondPct / firstPct - 1) * 100) : 0;
  return (
    <div className={className}>
      {[
        { k: "First half", v: data.halves.first, col: brand.textDis },
        { k: "Second half", v: data.halves.second, col: brand.amber },
      ].map((h) => (
        <div key={h.k} className="row">
          <div className="head">
            <span className="label">{h.k}</span>
            <span className="value">
              <strong>{h.v.pct}%</strong>
              <span className="frac">
                ({h.v.correct}/{h.v.total})
              </span>
            </span>
          </div>
          <div className="track">
            <div
              className="fill"
              style={{
                width: `${Math.min(100, h.v.pct * 3)}%`,
                background: h.col,
              }}
            />
          </div>
        </div>
      ))}
      {firstPct > 0 && (
        <div className="trend">
          <span aria-hidden>📈</span>
          <span>
            Accuracy {liftPct}% {liftPct >= 0 ? "higher" : "lower"} after the break
          </span>
        </div>
      )}
    </div>
  );
})(({ theme }) => ({
  background: brand.white,
  borderRadius: 12,
  padding: theme.spacing(1.75, 2),
  boxShadow: "0 1px 4px rgba(26,18,5,0.06)",

  "& .row": { marginBottom: 12 },
  "& .head": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 5,
  },
  "& .label": { fontSize: 13, color: brand.text, fontWeight: 600 },
  "& .value": { fontSize: 13, color: brand.textSec },
  "& .value strong": { color: brand.text, fontSize: 15, fontWeight: 800 },
  "& .frac": { marginLeft: 6, fontSize: 11 },
  "& .track": {
    height: 8,
    background: brand.sep,
    borderRadius: 4,
    overflow: "hidden",
  },
  "& .fill": {
    height: "100%",
    borderRadius: 4,
    transition: "width .3s",
  },
  "& .trend": {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 8,
    padding: "8px 10px",
    background: brand.amberFaint,
    borderRadius: 8,
    fontSize: 12,
    color: brand.amberDeep,
    fontWeight: 600,
  },
}));

// ── Decisiveness ────────────────────────────────────────────
export const DecisivenessCard = styled(({ data, className }) => (
  <div className={className}>
    <div className="stat">
      <div className="value">{data.decisiveness.firstAvg}</div>
      <div className="key">First half</div>
    </div>
    <div className="arrow" aria-hidden>
      →
    </div>
    <div className="stat highlight">
      <div className="value">{data.decisiveness.secondAvg}</div>
      <div className="key">Second half</div>
    </div>
  </div>
))(({ theme }) => ({
  background: brand.white,
  borderRadius: 12,
  padding: theme.spacing(1.75, 2),
  boxShadow: "0 1px 4px rgba(26,18,5,0.06)",
  display: "flex",
  alignItems: "center",
  gap: 10,

  "& .stat": { flex: 1, textAlign: "center" },
  "& .value": {
    fontSize: 24,
    fontWeight: 800,
    color: brand.text,
    lineHeight: 1,
  },
  "& .highlight .value": { color: brand.amberDeep },
  "& .key": {
    fontSize: 10,
    color: brand.textSec,
    marginTop: 4,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    fontWeight: 600,
  },
  "& .arrow": { fontSize: 18, color: brand.textDis },
}));

// ── Asterisk performance ────────────────────────────────────
export const AsteriskCard = styled(({ data, className }) => {
  const used = data.asterisks.used || 0;
  const correctPct = used > 0 ? (data.asterisks.correct / used) * 100 : 0;
  const wrongPct = used > 0 ? (data.asterisks.wrong / used) * 100 : 0;
  return (
    <div className={className}>
      <div className="head">
        <span className="emoji" aria-hidden>
          ⭐
        </span>
        <div className="summary">
          <div className="line-a">Used {used} times</div>
          <div className="line-b">Net impact on the group</div>
        </div>
        <div
          className="net"
          style={{
            color:
              data.asterisks.netPoints >= 0 ? brand.success : brand.error,
          }}
        >
          {data.asterisks.netPoints > 0 ? "+" : ""}
          {data.asterisks.netPoints} Points
        </div>
      </div>
      <div className="bar">
        <div className="bar-correct" style={{ width: `${correctPct}%` }} />
        <div className="bar-wrong" style={{ width: `${wrongPct}%` }} />
      </div>
      <div className="legend">
        <span className="ok">{data.asterisks.correct} correct</span>
        <span className="bad">{data.asterisks.wrong} wrong</span>
      </div>
    </div>
  );
})(({ theme }) => ({
  background: brand.white,
  borderRadius: 12,
  padding: theme.spacing(1.75, 2),
  boxShadow: "0 1px 4px rgba(26,18,5,0.06)",

  "& .head": {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 10,
  },
  "& .emoji": { fontSize: 20 },
  "& .summary": { flex: 1 },
  "& .line-a": { fontSize: 13, color: brand.text, fontWeight: 600 },
  "& .line-b": { fontSize: 11, color: brand.textSec, marginTop: 1 },
  "& .net": { fontSize: 18, fontWeight: 800 },
  "& .bar": {
    display: "flex",
    height: 10,
    borderRadius: 5,
    overflow: "hidden",
    background: brand.sep,
  },
  "& .bar-correct": { background: brand.success },
  "& .bar-wrong": { background: brand.error },
  "& .legend": {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 6,
    fontSize: 11,
  },
  "& .ok": { color: brand.success, fontWeight: 700 },
  "& .bad": { color: brand.error, fontWeight: 700 },
}));

// ── Confusion row ───────────────────────────────────────────
export const ConfusionRow = styled(({ round, actual, guessed, times, className }) => (
  <div className={className}>
    <div className="kicker">Round {round}</div>
    <div className="row">
      <div className="side">
        <div className="tag">Was</div>
        <div className="name">{actual}</div>
      </div>
      <div className="times">×{times}</div>
      <div className="side right">
        <div className="tag">Guessed</div>
        <div className="name wrong">{guessed}</div>
      </div>
    </div>
  </div>
))(({ theme }) => ({
  background: brand.white,
  borderRadius: 12,
  padding: theme.spacing(1.5, 1.75),
  boxShadow: "0 1px 4px rgba(26,18,5,0.06)",
  marginBottom: 8,

  "& .kicker": {
    fontSize: 10,
    fontWeight: 700,
    color: brand.textSec,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  "& .row": { display: "flex", alignItems: "center", gap: 10 },
  "& .side": { flex: 1, minWidth: 0 },
  "& .side.right": { textAlign: "right" },
  "& .tag": { fontSize: 10, color: brand.textSec },
  "& .name": { fontSize: 14, fontWeight: 700, color: brand.text },
  "& .name.wrong": { color: brand.error },
  "& .times": {
    fontSize: 11,
    fontWeight: 700,
    color: brand.amberDeep,
    padding: "4px 8px",
    borderRadius: 6,
    background: brand.amberLight,
  },
}));

// ── Comeback / Collapse cards ───────────────────────────────
export const MoverCard = styled(
  ({ tone, emoji, label, name, from, to, className }) => {
    const posTone = tone === "pos";
    const bg = posTone ? brand.amberLight : brand.errorBg;
    const fg = posTone ? brand.amberDeep : brand.error;
    const arrow = posTone ? "↑" : "↓";
    return (
      <div
        className={className}
        style={{ background: bg, border: `1px solid ${fg}30` }}
      >
        <div className="head">
          <span className="emoji" aria-hidden>
            {emoji}
          </span>
          <span className="label" style={{ color: fg }}>
            {label}
          </span>
        </div>
        <div className="name">{name}</div>
        <div className="sub">
          {from}
          <span className="arrow" style={{ color: fg }}>
            {" "}
            {arrow}{" "}
          </span>
          {to} correct
        </div>
      </div>
    );
  },
)({
  borderRadius: 12,
  padding: "12px 12px 14px",
  "& .head": { display: "flex", alignItems: "center", gap: 6 },
  "& .emoji": { fontSize: 16 },
  "& .label": {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },
  "& .name": {
    fontSize: 14,
    fontWeight: 700,
    color: brand.text,
    marginTop: 6,
    lineHeight: 1.2,
  },
  "& .sub": { fontSize: 11, color: brand.textSec, marginTop: 4 },
  "& .arrow": { fontWeight: 700 },
});

export const MoverGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
});

// ── Correct-order row ───────────────────────────────────────
export const CorrectOrderRow = styled(({ n, beer, isLast, className }) => (
  <div className={`${className}${isLast ? " is-last" : ""}`}>
    <div className="badge">{n}</div>
    <span className="name">{beer}</span>
  </div>
))({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 14px",
  borderBottom: `1px solid ${brand.sep}`,
  "&.is-last": { borderBottom: "none" },

  "& .badge": {
    width: 22,
    height: 22,
    borderRadius: 6,
    background: brand.amberLight,
    color: brand.amberDeep,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 800,
  },
  "& .name": { fontSize: 14, color: brand.text, fontWeight: 500 },
});
